/**
 * useBurn Hook
 *
 * Handles USDC bridge transactions on EVM and Solana chains.
 *
 * For EVM chains:
 * - Fetches a signed fee quote from the backend API
 * - Routes through ParseCctpBridge contract which collects fees and calls CCTP
 * - Supports paying network fee in native token (ETH) or USDC
 *
 * For Solana:
 * - Fetches a signed fee quote from the backend API (Ed25519 signature)
 * - Routes through FBV01 Anchor program which collects fees and CPIs to CCTP
 * - Supports paying network fee in native SOL or USDC
 */

import { useState, useCallback } from 'react';
import { Contract, parseUnits, formatUnits, zeroPadValue, hexlify, Signature, TypedDataEncoder, keccak256, AbiCoder } from 'ethers';
import toast from '@/utils/toast';
import {
  Connection,
  PublicKey,
  TransactionMessage,
  VersionedTransaction,
  AddressLookupTableAccount,
  SystemProgram,
  Keypair,
  Ed25519Program,
  SYSVAR_INSTRUCTIONS_PUBKEY,
} from '@solana/web3.js';
import { getAssociatedTokenAddress, TOKEN_PROGRAM_ID, TOKEN_2022_PROGRAM_ID } from '@solana/spl-token';
import { AnchorProvider, Program, BN } from '@coral-xyz/anchor';
import { useWallet as useSolanaWalletHook, useAnchorWallet, type AnchorWallet } from '@solana/wallet-adapter-react';
import {
  CCTP_CONFIG,
  ERC20_ABI,
  PARSE_BRIDGE_ABI,
  BRIDGE_CONTRACTS,
  SOLANA_BRIDGE_CONFIG,
  isBridgeContractDeployed,
  getDomainForChain,
  isEvmChain,
  isSolanaChain,
  Chain,
  type BridgeQuote,
} from '@parse/shared';
import { getErrorMessage, isUserRejection, getDisplayError, isReceiptUnknownMessage } from '@/utils/errors';
import { apiKeyHeader } from '@/services/api';
import { getEthersProviderSigner } from '@/config/ethers-adapter';
import { wagmiConfig } from '@/config/wagmi';
import { resolveEvmBridgeRoute } from '@/utils/smartAccount';
import { approveErc20Exact } from '@/utils/erc20Approve';
import {
  assertRecipientCodeAccepted,
  RecipientRiskDeclinedError,
  type RecipientRiskConfirm,
} from '@/utils/recipientCodeGuard';
import fbv02Idl from '@/idl/fbv02.json';

// Types
export type BurnStep =
  | 'idle'
  | 'fetching_quote'
  | 'switching_network'
  | 'approving'       // ERC-20 approve tx — CONTRACT-ACCOUNT path only; an EOA never enters this state
  | 'signing'         // EIP-712 typed data signing (was 'approving')
  | 'burning'
  | 'complete'
  | 'error';

export interface BurnState {
  isBurning: boolean;
  burnStep: BurnStep;
  burnTxHash: string | null;
  burnError: string | null;
}

export interface BurnParams {
  sourceChain: Chain;
  amount: string; // Human readable, e.g., "100"
  destinationChain: Chain;
  destinationAddress: string;
  payNative?: boolean; // Pay network fee in native token (ETH/SOL) instead of USDC
  /**
   * #144 (R2). Asks the user to accept a "code here, not there" recipient risk. REQUIRED, with no
   * default, deliberately: there are THREE distinct signing paths in this file (bridge(),
   * bridgeWithAuth(), and the Solana burn) and a guard that silently no-ops when nothing was wired
   * is a guard that gets dropped in a refactor without a single test going red.
   */
  confirmRecipientRisk: RecipientRiskConfirm;
}

/**
 * The server-built fee breakdown. This is what the UI RENDERS (FeeSection reads
 * `breakdown.networkFeeUsdc`), and it is built SEPARATELY from the signed quote —
 * `quote.ts:412` (quote) vs `quote.ts:498` (breakdown) are two independent objects.
 * Nothing structurally forces them to agree, which is why they are cross-checked
 * before any submit (see assertQuoteMatchesBreakdown).
 */
export interface QuoteBreakdown {
  grossAmount: string;
  percentageFeeBps: number;
  percentageFee: string;
  networkFeeUsdc: string;
  /** Circle fast-transfer pass-through; signed as `quote.maxFee`. */
  circleMaxFeeUsdc?: string;
  totalFeeUsdc: string;
  netAmount: string;
  networkFeeNative: string;
  networkFeeNativeDisplay: string;
  sourceChain: string;
  destChain: string;
  nativePrice: number;
}

// Quote response from the backend API
interface QuoteResponse {
  success: boolean;
  data?: {
    quote: BridgeQuote;
    signature: string;
    expiresAt: number;
    operatorPubkey?: string;
    breakdown: QuoteBreakdown;
  };
  error?: string;
}

/**
 * Normalise an atomic-units fee field to a canonical decimal string for comparison.
 *
 * The same logical value reaches the four submit sites in three different shapes: a JSON
 * string on `breakdown` and in the EVM quote tuples, a `BN` for the Solana Anchor arg
 * (`new BN(quote.networkFeeUsdc)`), and u64 little-endian bytes for the Solana Ed25519
 * message (`writeU64LE(BigInt(quote.networkFeeUsdc))`). Routing every input through BigInt
 * compares by VALUE, so "300", 300, 300n and BN(300) all agree and no comparison is ever
 * a BN against a string. Returns null for anything non-integral, which the caller treats
 * as a mismatch (fail closed — never compare two nulls into a false pass).
 */
export function normalizeAtomic(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  try {
    if (typeof v === 'bigint') return v.toString();
    if (typeof v === 'number') return Number.isInteger(v) ? BigInt(v).toString() : null;
    // Strings, BN and BigNumber-like objects all expose a decimal toString().
    const s = (typeof v === 'string' ? v : String(v)).trim();
    if (!/^-?\d+$/.test(s)) return null;
    return BigInt(s).toString();
  } catch {
    return null;
  }
}

/**
 * Cross-check the DISPLAYED fee breakdown against the SIGNED quote before submitting.
 *
 * WHY: the FE renders `feeEstimate.networkFeeUsdc` (from `breakdown`) but signs and submits
 * `quote.networkFeeUsdc`. Those are two independently-built server fields with no cross-check
 * anywhere in `apps/web`, so a compromised or buggy backend could display $0.30 and sign $9,999
 * and the user would authorise the larger number while reading the smaller one. Asserting
 * equality here makes the displayed number BINDING on what gets signed — informed consent
 * becomes real rather than theatrical, which is what the product's inform-don't-block
 * philosophy actually requires.
 *
 * Compares only fields that exist on BOTH objects, by value. It deliberately does NOT recompute
 * any fee from `grossAmount` and a bps rate: duplicating the backend's fee arithmetic in the FE
 * would add a second formula that can drift out of step with the server's and false-reject
 * legitimate transfers, while adding no protection (a backend that lies can lie consistently in
 * both fields). Pinning the signed RATE and the signed ABSOLUTE network fee pins the charge.
 *
 * Throws on mismatch — the caller must not submit.
 */
export function assertQuoteMatchesBreakdown(quote: BridgeQuote, breakdown: QuoteBreakdown): void {
  const mismatches: string[] = [];

  const check = (label: string, displayed: unknown, signed: unknown) => {
    const d = normalizeAtomic(displayed);
    const s = normalizeAtomic(signed);
    if (d === null || s === null || d !== s) {
      mismatches.push(`${label}: displayed=${String(displayed)} signed=${String(signed)}`);
    }
  };

  // The network fee — the field this whole check exists for. Rendered by FeeSection, signed
  // into the quote, deducted on-chain, and UNBOUNDED by MAX_FEE_BPS (which caps only the
  // percentage component). This is the one an inflated quote would abuse.
  check('networkFeeUsdc', breakdown.networkFeeUsdc, quote.networkFeeUsdc);

  // The percentage RATE. The signed quote carries only `percentageFeeBps`; there is no absolute
  // percentage-fee field on it to compare `breakdown.percentageFee` against. The contract charges
  // amount x bps / 1e4 from the SIGNED rate, so pinning the rate pins the charge — without this
  // file duplicating the fee arithmetic.
  check('percentageFeeBps', breakdown.percentageFeeBps, quote.percentageFeeBps);

  // Circle's fast-transfer pass-through: displayed as circleMaxFeeUsdc, signed as quote.maxFee.
  if (breakdown.circleMaxFeeUsdc !== undefined) {
    check('circleMaxFee', breakdown.circleMaxFeeUsdc, quote.maxFee);
  }

  // Signed, and sent as the raw tx value on the payNative path — so a mismatch here is spendable.
  check('networkFeeNative', breakdown.networkFeeNative, quote.networkFeeNative);

  if (mismatches.length > 0) {
    console.error('QUOTE_BREAKDOWN_MISMATCH', { mismatches, quote, breakdown });
    throw new Error(
      'Fee mismatch: the fee shown does not match the fee in the signed quote. Refresh and try again.'
    );
  }
}

// Result of a burn operation, includes quoted fee data for underpricing detection
type BurnResult = { txHash: string; networkFeeUsdc: string; nativePriceUsd: number };

// API base URL for quote fetching
const API_BASE = import.meta.env.VITE_API_URL || '';

/**
 * Bounded wait for a burn receipt.
 *
 * ── WHAT IS AND IS NOT FIXED HERE — READ BEFORE CHANGING ─────────────────────────────────────────
 * ethers v6 `sendTransaction` and `wait()` accept no timeout. The ONLY mechanism available is
 * `Promise.race`, which does NOT cancel the in-flight poll and leaves an orphaned promise behind.
 *
 * It is applied to `wait()` and DELIBERATELY NOT to the send (`bridgeContract.bridge(...)` /
 * `bridgeWithAuth(...)`). The difference is human time:
 *
 *  - The SEND await spans the user confirming in their wallet, which is legitimately unbounded — a
 *    user reading the prompt, a hardware wallet, a co-signer. Racing it would fire a "timed out"
 *    error at anyone slow to confirm, and if they then confirmed a second later the transaction
 *    would land while the UI claimed otherwise. That manufactures exactly the false-terminal-state
 *    defect this round exists to delete, and on the EOA path (no allowance to be consumed) it can
 *    lead to a genuine second burn. A bounded hang is not worth a fabricated failure.
 *  - By the time `wait()` is awaited the send has RESOLVED, so we hold a real tx hash and all
 *    remaining time is chain time. Bounding it converts an unbounded spinner into a hash we can
 *    hand to the registration pipeline — which is what actually gets the mint completed.
 *
 * On timeout we therefore return the hash and let the backend adjudicate: `/hybrid` re-reads the
 * receipt on-chain and rejects a burn that is missing or reverted, so nothing is taken on trust.
 * The user is told confirmation is still pending — never that it failed, and never invited to
 * resubmit.
 *
 * Consequence, stated plainly: this does NOT make the Safe-over-WalletConnect cohort work. Their
 * hang is at the SEND, which is intentionally untouched.
 */
const BURN_RECEIPT_TIMEOUT_MS = 180_000;

interface SentTx { hash: string; wait: () => Promise<{ hash: string; status: number; gasUsed: bigint }> }

async function waitForBurnReceiptBounded(
  tx: SentTx,
  label: string,
): Promise<{ hash: string; confirmed: boolean }> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), BURN_RECEIPT_TIMEOUT_MS);
  });

  const waiting = tx.wait();
  // The race can settle on the deadline while `waiting` is still pending; if it later REJECTS, that
  // rejection has no handler and would surface as an unhandled promise rejection. Attaching a no-op
  // catch to the same promise object marks it handled without affecting the race below.
  waiting.catch(() => undefined);

  try {
    const receipt = await Promise.race([waiting, deadline]);
    if (receipt) {
      console.log('BRIDGE_TX_CONFIRMED', {
        hash: receipt.hash, status: receipt.status, gasUsed: receipt.gasUsed.toString(),
      });
      return { hash: receipt.hash, confirmed: true };
    }
    console.warn('BRIDGE_TX_CONFIRMATION_TIMEOUT', { label, hash: tx.hash });
    toast.warning('Still confirming your transfer on-chain — this is taking longer than usual. Do not resubmit.');
    return { hash: tx.hash, confirmed: false };
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

// DOMAIN_SEPARATOR cache for EIP-3009 signing (one per chain)
// Stores both the on-chain DS and the domain object used for EIP-712 signing
interface CachedDomain {
  domainSeparator: string;
  domain: { name: string; version: string; chainId: number; verifyingContract: string };
}
const domainSeparatorCache = new Map<string, CachedDomain>();
const USDC_DS_ABI = [
  'function DOMAIN_SEPARATOR() view returns (bytes32)',
  'function name() view returns (string)',
  'function version() view returns (string)',
];

/**
 * Fetch a signed quote from the backend API
 * This quote must be used within 15 minutes and the signature verified by the contract
 */
async function fetchQuote(
  sourceChain: Chain,
  destChain: Chain,
  amount: string, // USDC in smallest units (6 decimals)
  userAddress: string
): Promise<QuoteResponse> {
  const base = API_BASE || window.location.origin;
  const url = new URL(`${base}/api/quote`);
  url.searchParams.set('sourceChain', sourceChain);
  url.searchParams.set('destChain', destChain);
  url.searchParams.set('amount', amount);
  url.searchParams.set('userAddress', userAddress);

  // Pass referral code from localStorage — attribution happens at bridge time
  const ref = localStorage.getItem('parsebridge:ref');
  if (ref) {
    url.searchParams.set('ref', ref);
  }

  try {
    const response = await fetch(url.toString(), { headers: apiKeyHeader });
    return await response.json();
  } catch {
    return { success: false, error: 'Server unavailable. Please try again later.' };
  }
}

// Solana configuration — network-aware fallback (mirrors apps/web/src/main.tsx). NEVER silently fall back to
// DEVNET in a mainnet build: a devnet connection makes a mainnet burn fail with "address table account doesn't
// exist" / stale-blockhash reverts. When VITE_SOLANA_RPC_URL is unset, pick the cluster matching VITE_NETWORK.
const SOLANA_RPC_URL = import.meta.env.VITE_SOLANA_RPC_URL
  || (import.meta.env.VITE_NETWORK === 'mainnet'
    ? 'https://api.mainnet-beta.solana.com'
    : 'https://api.devnet.solana.com');

/**
 * Browser-compatible helper to convert hex string to Uint8Array
 */
function hexToBytes(hex: string): Uint8Array {
  const cleanHex = hex.startsWith('0x') ? hex.slice(2) : hex;
  const bytes = new Uint8Array(cleanHex.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(cleanHex.substr(i * 2, 2), 16);
  }
  return bytes;
}

/**
 * Browser-compatible helper to convert Uint8Array to hex string
 */
function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Browser-compatible helper to encode string to Uint8Array
 */
function stringToBytes(str: string): Uint8Array {
  return new TextEncoder().encode(str);
}

/**
 * Browser-compatible helper to write u16 little-endian
 */
function writeU16LE(value: number): Uint8Array {
  const bytes = new Uint8Array(2);
  bytes[0] = value & 0xff;
  bytes[1] = (value >> 8) & 0xff;
  return bytes;
}

/**
 * Browser-compatible helper to write u32 little-endian
 */
function writeU32LE(value: number): Uint8Array {
  const bytes = new Uint8Array(4);
  bytes[0] = value & 0xff;
  bytes[1] = (value >> 8) & 0xff;
  bytes[2] = (value >> 16) & 0xff;
  bytes[3] = (value >> 24) & 0xff;
  return bytes;
}

/**
 * Browser-compatible helper to write u64 little-endian
 */
function writeU64LE(value: bigint): Uint8Array {
  const bytes = new Uint8Array(8);
  for (let i = 0; i < 8; i++) {
    bytes[i] = Number((value >> BigInt(i * 8)) & BigInt(0xff));
  }
  return bytes;
}

/**
 * Browser-compatible helper to write i64 little-endian (signed)
 */
function writeI64LE(value: bigint): Uint8Array {
  const bytes = new Uint8Array(8);
  for (let i = 0; i < 8; i++) {
    bytes[i] = Number((value >> BigInt(i * 8)) & BigInt(0xff));
  }
  return bytes;
}

/**
 * Browser-compatible helper to concatenate Uint8Arrays
 */
function concatBytes(...arrays: Uint8Array[]): Uint8Array {
  const totalLength = arrays.reduce((sum, arr) => sum + arr.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const arr of arrays) {
    result.set(arr, offset);
    offset += arr.length;
  }
  return result;
}

// Simple base58 decoder for Solana addresses
function decodeBase58(str: string): Uint8Array {
  const ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
  const bytes: number[] = [0];

  for (const char of str) {
    let carry = ALPHABET.indexOf(char);
    if (carry === -1) throw new Error(`Invalid base58 character: ${char}`);

    for (let i = 0; i < bytes.length; i++) {
      const value = bytes[i] * 58 + carry;
      bytes[i] = value % 256;
      carry = Math.floor(value / 256);
    }

    while (carry > 0) {
      bytes.push(carry % 256);
      carry = Math.floor(carry / 256);
    }
  }

  // Add leading zeros for each leading '1' in the input
  for (const char of str) {
    if (char !== '1') break;
    bytes.push(0);
  }

  return new Uint8Array(bytes.reverse());
}

/**
 * User-facing Solana recipient errors (#147 / S1). Exported so tests assert the exact strings a user
 * reads — the DISTINCTION between them is the point, not the wording of any one of them.
 */
export const SOLANA_RECIPIENT_INVALID =
  'That is not a valid Solana address. Paste your Solana wallet address.';
export const SOLANA_RECIPIENT_OFF_CURVE =
  'That looks like a token account, not a wallet. Paste your Solana wallet address (the one your wallet app shows), not a USDC account address.';
export const SOLANA_RECIPIENT_IS_TOKEN_ACCOUNT =
  'That address is a USDC token account, not a wallet. Paste your Solana wallet address (the one your wallet app shows) — funds sent to a token account cannot be recovered.';
export const SOLANA_RECIPIENT_UNVERIFIED =
  'Could not verify the Solana recipient address right now — please try again in a moment.';

/**
 * Reject an ON-CURVE address that is actually an SPL token account (#147 / S1).
 *
 * ── WHY THE CURVE CHECK ALONE IS NOT ENOUGH ──────────────────────────────────────────────────────
 * `getAssociatedTokenAddress(..., allowOwnerOffCurve = false)` rejects OFF-curve owners, and every
 * ATA is off-curve — so pasting an ATA already failed. But a keypair-created SPL token account
 * (`createAccount` + `initializeAccount`, the pre-ATA pattern some exchanges still use) is
 * ON-CURVE. It passed with no flag change, and funds minted to `ATA(owner = tokenAccountPubkey)` —
 * an authority that can never sign. Permanent strand, live, with nothing gating it.
 *
 * ── AND THERE IS NO BACKEND BACKSTOP ─────────────────────────────────────────────────────────────
 * The frontend is the ONLY guard in the entire pipeline: `solana-mint.ts` derives with
 * `allowOwnerOffCurve = true` and creates the ATA IDEMPOTENTLY for any owner, `address-validator.ts`
 * does a bare `new PublicKey()` with no curve check, and the Anchor program passes
 * `recipient: [u8;32]` through raw. The backend will cheerfully create the ATA and mint into it.
 *
 * ── THE CHECK IS ADDITIVE, NEVER A REPLACEMENT ───────────────────────────────────────────────────
 * This runs only for ON-CURVE addresses, in ADDITION to the curve check — never instead of it.
 * Flipping `allowOwnerOffCurve` to true to "support Squads vaults" would delete the only guard and
 * newly permit a pasted ATA, a CLOSED token account (returns null here, indistinguishable from a
 * legitimate PDA), a token-2022 account, an executable program id, and — since roughly half of
 * garbled base58 is off-curve — a large share of paste typos, converting them from "error before
 * burn" into "permanent loss after burn". This check can prove an address IS a token account; it
 * can never prove an off-curve address is SPENDABLE. That is undecidable by inspection.
 *
 * ── FAIL CLOSED, BUT SAY WHICH FAILURE IT IS ─────────────────────────────────────────────────────
 * This is the FIRST Solana RPC call the EVM→Solana path has ever made (`new Connection` previously
 * appeared exactly once in this file, on the Solana-SOURCE path), and it sits in front of the
 * majority happy case. An RPC failure therefore blocks a legitimate transfer, which is the right
 * trade for a permanent-strand risk — but the message MUST be "could not verify, try again", never
 * "invalid address". Rendering a network blip as a rejected address is the same false-assertion
 * defect the G3 copy fix exists to delete.
 */
async function assertSolanaRecipientIsWallet(owner: PublicKey, address: string): Promise<void> {
  const tokenProgramIds = [TOKEN_PROGRAM_ID.toBase58(), TOKEN_2022_PROGRAM_ID.toBase58()];

  let info: Awaited<ReturnType<Connection['getAccountInfo']>>;
  try {
    info = await new Connection(SOLANA_RPC_URL, 'confirmed').getAccountInfo(owner);
  } catch (err) {
    console.warn('[useBurn] Solana recipient owner check failed:', getErrorMessage(err));
    throw new Error(SOLANA_RECIPIENT_UNVERIFIED);
  }

  // `null` = the account does not exist on-chain. That is the NORMAL state of a fresh wallet that
  // has never been funded, and it is not evidence of anything bad. Allow it.
  if (!info) return;

  if (tokenProgramIds.includes(info.owner.toBase58())) {
    console.warn('[useBurn] Solana recipient is an SPL token account, refusing:', {
      address, ownerProgram: info.owner.toBase58(),
    });
    throw new Error(SOLANA_RECIPIENT_IS_TOKEN_ACCOUNT);
  }
}

/**
 * Convert an address to bytes32 format for CCTP
 *
 * IMPORTANT: For Solana destinations, CCTP requires the mintRecipient to be
 * the USDC ATA (Associated Token Account), NOT the wallet address.
 * See: https://developers.circle.com/cctp/solana-programs
 */
async function addressToBytes32(address: string, destinationChain: Chain): Promise<string> {
  if (isEvmChain(destinationChain)) {
    // EVM address: 0x prefix + 20 bytes, pad to 32 bytes
    return zeroPadValue(address, 32);
  } else if (isSolanaChain(destinationChain)) {
    // Solana destination: mintRecipient must be the USDC ATA, not wallet address
    // The ATA is where CCTP will mint tokens on Solana
    let walletPubkey: PublicKey;
    try {
      walletPubkey = new PublicKey(address);
    } catch {
      throw new Error(SOLANA_RECIPIENT_INVALID);
    }
    const usdcMint = new PublicKey(CCTP_CONFIG.solana.usdcMint);

    // Derive the ATA for this wallet's USDC account. `allowOwnerOffCurve` stays at its DEFAULT of
    // false — this throw is the off-curve guard and it is deliberately preserved (#147 / S2); only
    // the raw `TokenOwnerOffCurveError` it produced is replaced with something a user can act on.
    // Ordered first because it costs no RPC: garbage and pasted ATAs are rejected for free.
    let ata: PublicKey;
    try {
      ata = await getAssociatedTokenAddress(usdcMint, walletPubkey);
    } catch (err) {
      console.warn('[useBurn] Solana recipient rejected as off-curve:', getErrorMessage(err));
      throw new Error(SOLANA_RECIPIENT_OFF_CURVE);
    }

    // #147 / S1 — the ON-CURVE token-account hole the curve check cannot see.
    await assertSolanaRecipientIsWallet(walletPubkey, address);

    console.log('Solana mintRecipient derivation:', {
      walletAddress: address,
      usdcMint: CCTP_CONFIG.solana.usdcMint,
      derivedAta: ata.toBase58(),
    });

    // Return ATA as bytes32
    return hexlify(ata.toBytes());
  }
  throw new Error(`Unsupported destination chain: ${destinationChain}`);
}

/**
 * Convert destination address to raw bytes for Solana instruction
 */
function addressToBytes32Raw(address: string, destinationChain: Chain): Uint8Array {
  if (isEvmChain(destinationChain)) {
    // EVM address: pad 20 bytes to 32 bytes (left pad with zeros)
    const addressBytes = hexToBytes(address);
    const padded = new Uint8Array(32);
    padded.set(addressBytes, 32 - addressBytes.length);
    return padded;
  } else if (isSolanaChain(destinationChain)) {
    // Solana address: base58 decode (already 32 bytes)
    return decodeBase58(address);
  }
  throw new Error(`Unsupported destination chain: ${destinationChain}`);
}

/**
 * Build the 136-byte quote message for Ed25519 signature verification.
 * Must exactly match the Rust build_quote_message() and backend buildSolanaQuoteMessage().
 *
 * Format (all little-endian):
 * - dest_domain: 4 bytes (u32)         [offset 0]
 * - network_fee_usdc: 8 bytes (u64)    [offset 4]
 * - network_fee_native: 8 bytes (u64)  [offset 12]
 * - percentage_fee_bps: 2 bytes (u16)  [offset 20]
 * - timestamp: 8 bytes (i64)           [offset 22]
 * - nonce: 8 bytes (u64)               [offset 30]
 * - user_address: 32 bytes (pubkey)    [offset 38]
 * - program_id: 32 bytes (pubkey)      [offset 70]
 * - referrer_address: 32 bytes (pubkey, zeros when no referrer) [offset 102]
 * - referrer_share_bps: 2 bytes (u16, 0 when no referrer)      [offset 134]
 * TOTAL: 136 bytes
 */
function buildSolanaQuoteMessage(
  quote: BridgeQuote,
  userPubkey: PublicKey
): Uint8Array {
  const programId = new PublicKey(SOLANA_BRIDGE_CONFIG.programId);

  // Referrer address: decode from quote or use 32 zero bytes (Pubkey::default())
  let referrerBytes: Uint8Array;
  if (quote.referrerAddress && quote.referrerAddress !== '' && quote.referrerAddress !== '11111111111111111111111111111111') {
    referrerBytes = new PublicKey(quote.referrerAddress).toBytes();
  } else {
    referrerBytes = new Uint8Array(32); // Pubkey::default() = all zeros
  }

  return concatBytes(
    writeU32LE(quote.destDomain),
    writeU64LE(BigInt(quote.networkFeeUsdc)),
    writeU64LE(BigInt(quote.networkFeeNative)),
    writeU16LE(quote.percentageFeeBps),
    writeI64LE(BigInt(quote.timestamp)),
    writeU64LE(BigInt(quote.nonce)),
    userPubkey.toBytes(),
    programId.toBytes(),
    referrerBytes,
    writeU16LE(quote.referrerShareBps ?? 0)
  );
}

// EVM provider functions removed — replaced by getEthersProviderSigner from @/config/ethers-adapter

// Solana provider function removed — replaced by useAnchorWallet/useSolanaWallet hooks

/**
 * Execute an EVM burn through `ParseCctpBridge.bridge()` — the CONTRACT-ACCOUNT path (#137 Phase 1).
 *
 * `bridge()` authenticates on `quote.userAddress == msg.sender` (ParseCctpBridge.sol:389) and needs
 * NO user signature, which is what makes it usable by a smart wallet that cannot produce a 65-byte
 * ECDSA signature over its own address. The cost is that it pulls funds with `transferFrom`, so it
 * needs an ERC-20 allowance first — hence two transactions instead of one.
 *
 * SECURITY — every invariant the EOA path enforces is enforced here too, and for the same reasons:
 *  - the operator-signed `quote` is passed through UNCHANGED and re-verified on-chain; we never
 *    reconstruct or adjust any fee field, so fee math is entirely the contract's and the backend's
 *  - the pre-flight balance check and the pre-broadcast expiry re-check are the same checks, in the
 *    same order, with the same messages as STEP 4 / STEP 5 of the EOA path
 *  - the approval is for EXACTLY `amount` and no more, to the bridge contract only
 *  - `MIN_AMOUNT` is the contract's business. A $1 burn reverting once fees are deducted is EXPECTED
 *    behaviour, identical on both paths — do not "fix" it here.
 *
 * Deliberately absent: the V9-1 auth-nonce destination binding. `bridge()`'s NatSpec
 * (ParseCctpBridge.sol:360-365) states the asymmetry is intentional — the caller IS the user and
 * chooses their own recipient, so no third party can redirect the burn and no binding is needed.
 */
async function executeEvmBridgeViaApproval(args: {
  signer: Awaited<ReturnType<typeof getEthersProviderSigner>>['signer'];
  signerAddress: string;
  chainId: number;
  usdcToken: string;
  bridgeContractAddress: string;
  amountBigInt: bigint;
  destinationChain: Chain;
  destinationAddress: string;
  payNative: boolean;
  quote: BridgeQuote;
  signature: string;
  expiresAt: number;
  breakdown: QuoteBreakdown;
  setStep: (step: BurnStep) => void;
  confirmRecipientRisk: RecipientRiskConfirm;
}): Promise<BurnResult> {
  const {
    signer, signerAddress, chainId, usdcToken, bridgeContractAddress, amountBigInt,
    destinationChain, destinationAddress, payNative, quote, signature, expiresAt, breakdown, setStep,
    confirmRecipientRisk,
  } = args;

  // Displayed-vs-signed cross-check at the submit site itself (#161 item 2). The caller already
  // ran this on the same pair, so in the current call graph it can never fire here — it is kept
  // because this function takes `quote` and `breakdown` as two SEPARATE arguments, so it is the
  // boundary where a future caller could pass a mismatched pair. Cheap, and it fails closed.
  assertQuoteMatchesBreakdown(quote, breakdown);

  // ── Balance check — same check, same message as the EOA path ────────────────────────────────────
  const usdcContract = new Contract(usdcToken, ERC20_ABI, signer);
  const balance = await usdcContract.balanceOf(signerAddress);
  const balanceBigInt = BigInt(balance.toString());
  if (balanceBigInt < amountBigInt) {
    throw new Error(
      `Insufficient USDC balance: have ${formatUnits(balanceBigInt, 6)}, need ${formatUnits(amountBigInt, 6)}`
    );
  }

  // ── #144 GUARD — signing path 1 of 3 (contract-account bridge() route) ──────────────────────────
  // Runs against the EXACT (recipient, destChain) pair encoded one line below, and BEFORE the
  // approve, so declining costs the user nothing. See utils/recipientCodeGuard.ts for the predicate.
  await assertRecipientCodeAccepted({
    recipient: destinationAddress, destChain: destinationChain, confirm: confirmRecipientRisk,
  });

  const mintRecipient = await addressToBytes32(destinationAddress, destinationChain);

  // ── Approve EXACTLY `amount` to the bridge ──────────────────────────────────────────────────────
  // Reuses the DEX path's hardened approve (never MAX, USDT-class reset-to-0, post-allowance verify
  // with backoff for RPC read-after-write lag). It NO-OPS when the allowance already covers `amount`,
  // which is what makes the "approve succeeded, user then abandoned the bridge" retry cheap and safe:
  // the second attempt re-reads the allowance, skips the approve, and goes straight to the wallet
  // prompt for bridge(). It never blindly re-approves.
  await approveErc20Exact(
    wagmiConfig,
    usdcToken as `0x${string}`,
    bridgeContractAddress as `0x${string}`,
    signerAddress as `0x${string}`,
    amountBigInt,
    chainId,
    () => setStep('approving'),
  );

  setStep('burning');

  const bridgeContract = new Contract(bridgeContractAddress, PARSE_BRIDGE_ABI, signer);

  const quoteTuple = {
    destDomain: quote.destDomain,
    networkFeeUsdc: quote.networkFeeUsdc,
    networkFeeNative: quote.networkFeeNative,
    percentageFeeBps: quote.percentageFeeBps,
    timestamp: quote.timestamp,
    nonce: quote.nonce,
    userAddress: quote.userAddress,
    maxFee: quote.maxFee,
    minFinalityThreshold: quote.minFinalityThreshold,
    referrerAddress: quote.referrerAddress || '0x0000000000000000000000000000000000000000',
    referrerShareBps: quote.referrerShareBps ?? 0,
  };

  const nativeValue = payNative ? BigInt(quote.networkFeeNative) : 0n;

  console.log('BRIDGE_CALL', {
    amount: amountBigInt.toString(),
    recipient: mintRecipient,
    quote: quoteTuple,
    payNative,
    nativeValue: nativeValue.toString(),
    breakdown: {
      percentageFee: breakdown.percentageFee,
      networkFeeUsdc: breakdown.networkFeeUsdc,
      totalFee: breakdown.totalFeeUsdc,
      netAmount: breakdown.netAmount,
    },
  });

  // Re-check expiry right before the transaction. This matters MORE here than on the EOA path: the
  // approve tx sat between the quote fetch and this call, so materially more wall-clock has elapsed.
  const currentTime = Math.floor(Date.now() / 1000);
  if (currentTime > expiresAt) {
    throw new Error('Quote expired during transaction preparation. Please try again.');
  }

  const bridgeTx = await bridgeContract.bridge(
    amountBigInt,
    mintRecipient,
    quoteTuple,
    signature,       // operator signature from quote
    payNative,
    { value: nativeValue }
  );

  console.log('BRIDGE_TX_SENT', { hash: bridgeTx.hash });

  const { hash } = await waitForBurnReceiptBounded(bridgeTx, 'bridge');

  return { txHash: hash, networkFeeUsdc: breakdown.networkFeeUsdc, nativePriceUsd: breakdown.nativePrice };
}

/**
 * Execute EVM burn transaction via ParseCctpBridge
 *
 * Flow:
 * 1. Fetch signed quote from backend API
 * 2. Verify quote validity (expiry, user address)
 * 3. Switch network if needed
 * 3.5 Route: contract account → bridge() (approve + bridge); EOA → the EIP-3009 path below, unchanged
 * 4. Sign EIP-3009 ReceiveWithAuthorization (EIP-712 typed data — no approve tx needed)
 * 5. Call bridgeWithAuth() with quote, operator signature, and EIP-3009 auth params
 * 6. Contract verifies signatures, pulls USDC via receiveWithAuthorization, collects fees, calls CCTP
 */
async function executeEvmBurn(
  params: BurnParams,
  setStep: (step: BurnStep) => void
): Promise<BurnResult> {
  const {
    sourceChain,
    amount,
    destinationChain,
    destinationAddress,
    payNative = false,
    confirmRecipientRisk,
  } = params;

  const evmChain = sourceChain as 'ethereum' | 'base' | 'optimism' | 'arbitrum' | 'polygon';
  const config = CCTP_CONFIG[evmChain];
  const bridgeContractAddress = BRIDGE_CONTRACTS[evmChain];

  // Convert amount to smallest units — CCTP-only path, USDC is always 6 decimals on
  // CCTP chains. BSC never routes through this codepath (deBridge DLN only).
  const amountInSmallestUnits = parseUnits(amount, 6);
  const amountBigInt = BigInt(amountInSmallestUnits.toString());

  // Get provider and signer via wagmi ethers adapter
  let { provider, signer } = await getEthersProviderSigner();
  const signerAddress = await signer.getAddress();

  console.log('BRIDGE_CONFIG_DEBUG', {
    sourceChain,
    bridgeContract: bridgeContractAddress,
    usdcToken: config.usdcToken,
    amount,
    amountInSmallestUnits: amountBigInt.toString(),
    payNative,
  });

  // ─────────────────────────────────────────────────────────────────────
  // STEP 1: Check if bridge contract is deployed
  // ─────────────────────────────────────────────────────────────────────
  if (!isBridgeContractDeployed(evmChain)) {
    throw new Error(
      `ParseCctpBridge contract not deployed on ${evmChain}. ` +
      `Please update BRIDGE_CONTRACTS in @parse/shared after deployment.`
    );
  }

  // ─────────────────────────────────────────────────────────────────────
  // STEP 2: Fetch signed quote from backend
  // ─────────────────────────────────────────────────────────────────────
  setStep('fetching_quote');

  const quoteResponse = await fetchQuote(
    sourceChain,
    destinationChain,
    amountBigInt.toString(),
    signerAddress
  );

  if (!quoteResponse.success || !quoteResponse.data) {
    throw new Error(quoteResponse.error || 'Failed to fetch quote from server');
  }

  const { quote, signature, expiresAt, breakdown } = quoteResponse.data;

  console.log('QUOTE_RECEIVED', {
    quote,
    expiresAt,
    breakdown,
    timeUntilExpiry: expiresAt - Math.floor(Date.now() / 1000),
  });

  // Displayed-vs-signed cross-check (#161 item 2) — BEFORE the EIP-3009 signature and before the
  // bridgeWithAuth submit, and before the bridge()-via-approval branch below, so it covers BOTH
  // EVM submit sites. Refuses to submit when the rendered fee is not the fee being signed.
  assertQuoteMatchesBreakdown(quote, breakdown);

  // Verify quote is for this user (sanity check - contract will verify too)
  if (quote.userAddress.toLowerCase() !== signerAddress.toLowerCase()) {
    throw new Error('Quote user address mismatch');
  }

  // Check if quote is about to expire (less than 2 minutes remaining)
  const timeRemaining = expiresAt - Math.floor(Date.now() / 1000);
  if (timeRemaining < 0) {
    throw new Error('Quote has expired. Please request a new quote.');
  }
  if (timeRemaining < 120) {
    console.warn('QUOTE_EXPIRING_SOON', { timeRemaining });
    toast.warning(`Quote expires in ${Math.floor(timeRemaining / 60)}m ${timeRemaining % 60}s`);
  }

  // ─────────────────────────────────────────────────────────────────────
  // STEP 3: Validate network and switch if needed
  // ─────────────────────────────────────────────────────────────────────
  const network = await provider.getNetwork();
  const expectedChainId = config.chainId;
  const connectedChainId = Number(network.chainId);

  if (connectedChainId !== expectedChainId) {
    setStep('switching_network');

    const hexChainId = `0x${expectedChainId.toString(16)}`;

    // Switch via the RAW wallet RPC, NOT wagmi's switchChain. getConnectorClient({chainId}) — used by
    // getEthersProviderSigner(chainId) — throws ConnectorChainMismatchError ("the current chain of the
    // connector (id: X) does not match the connection's chain (id: Y)") while the connector lags, and
    // wagmi's switchChain can surface that same mismatch instead of actually switching. Strategy:
    // switch-first; on ANY non-rejection failure (typically 4902 = chain not in the wallet) add-and-
    // switch via the wagmi/viem chain def. (Don't sniff for 4902: an ethers-wrapped wallet error can
    // hide the code, and wallet_addEthereumChain on an already-present chain is a harmless switch.)
    try {
      await provider.send('wallet_switchEthereumChain', [{ chainId: hexChainId }]);
    } catch (switchError) {
      if (isUserRejection(switchError)) {
        throw new Error('Network switch rejected by user');
      }
      const chainDef = wagmiConfig.chains.find((c) => c.id === expectedChainId);
      if (!chainDef) {
        throw new Error(`${sourceChain} network is not added to your wallet (no chain definition available to add it).`);
      }
      try {
        await provider.send('wallet_addEthereumChain', [{
          chainId: hexChainId,
          chainName: chainDef.name,
          nativeCurrency: chainDef.nativeCurrency,
          rpcUrls: [...chainDef.rpcUrls.default.http],
          ...(chainDef.blockExplorers?.default?.url
            ? { blockExplorerUrls: [chainDef.blockExplorers.default.url] }
            : {}),
        }]);
        // wallet_addEthereumChain usually switches too; make sure we land on it.
        await provider.send('wallet_switchEthereumChain', [{ chainId: hexChainId }]).catch(() => undefined);
      } catch (addError) {
        if (isUserRejection(addError)) {
          throw new Error(`Adding the ${sourceChain} network was rejected.`);
        }
        // Never surface the raw ethers wrapper (e.g. "could not coalesce error (error={…}, code=4901)") to
        // the UI — it's noise to the user. Log the detail for debugging and show a clean, ACTIONABLE message
        // pointing at the one manual step that resolves it (add/select the network in the wallet).
        console.warn('[useBurn] wallet_addEthereumChain failed for chain:', sourceChain, getErrorMessage(addError));
        throw new Error(`Couldn't switch your wallet to the ${sourceChain} network. Please add or select ${sourceChain} in your wallet, then try again.`);
      }
    }

    // Re-derive the ethers signer on the new chain. getEthersProviderSigner() WITHOUT a chainId reads
    // the connector's CURRENT chain (no ConnectorChainMismatchError assertion); poll until it reports
    // the target chain, since the connector's chainChanged event can lag the switch's resolve.
    let switched = false;
    for (let attempt = 0; attempt < 12; attempt++) {
      try {
        ({ provider, signer } = await getEthersProviderSigner());
        if (Number((await provider.getNetwork()).chainId) === expectedChainId) {
          switched = true;
          break;
        }
      } catch {
        // connector not yet synced to the new chain — retry
      }
      await new Promise((resolve) => setTimeout(resolve, 300));
    }
    if (!switched) {
      throw new Error(`Wallet did not switch to ${sourceChain} (chain ${expectedChainId}). Please switch it in your wallet and try again.`);
    }
    console.log('NETWORK_SWITCHED', { chainId: expectedChainId });
  }

  // ═════════════════════════════════════════════════════════════════════
  // STEP 3.5: ROUTE — contract accounts take bridge(), EOAs take bridgeWithAuth() (#137 Phase 1)
  // ═════════════════════════════════════════════════════════════════════
  // Everything above this point is shared. Everything BELOW this block is the untouched EOA path:
  // if `resolveEvmBridgeRoute` says 'bridgeWithAuth' — which includes the flag being off, an RPC
  // failure reading code, and every proven-EOA case — control falls straight through and the code
  // that runs is byte-for-byte what ran before Phase 1. That invariant is the point of putting the
  // branch here rather than threading conditionals through STEP 4/5.
  const routeDecision = await resolveEvmBridgeRoute({
    address: signerAddress as `0x${string}`,
    chainId: expectedChainId,
  });
  console.log('BRIDGE_ROUTE_DECISION', { route: routeDecision.route, reason: routeDecision.reason });

  if (routeDecision.route === 'bridge') {
    return await executeEvmBridgeViaApproval({
      signer,
      signerAddress,
      chainId: expectedChainId,
      usdcToken: config.usdcToken,
      bridgeContractAddress,
      amountBigInt,
      destinationChain,
      destinationAddress,
      payNative,
      quote,
      signature,
      expiresAt,
      breakdown,
      setStep,
      confirmRecipientRisk,
    });
  }

  // ─────────────────────────────────────────────────────────────────────
  // STEP 4: Sign EIP-3009 ReceiveWithAuthorization (replaces approve)
  // ─────────────────────────────────────────────────────────────────────
  setStep('signing');

  const usdcContract = new Contract(config.usdcToken, ERC20_ABI, signer);

  // Check balance before signing
  const balance = await usdcContract.balanceOf(signerAddress);
  const balanceBigInt = BigInt(balance.toString());

  if (balanceBigInt < amountBigInt) {
    // #102: show HUMAN USDC amounts, not raw 6-dec atomic subunits. Both values are 6-dec USDC
    // (balanceOf / parseUnits(amount, 6)); .toString() on the raw bigint printed "need 100000000"
    // for a 100 USDC transfer. formatUnits(_, 6) renders "need 100".
    throw new Error(
      `Insufficient USDC balance: have ${formatUnits(balanceBigInt, 6)}, need ${formatUnits(amountBigInt, 6)}`
    );
  }

  // ── #144 GUARD — signing path 2 of 3 (EOA bridgeWithAuth() route, ~100% of production) ──────────
  // Before the EIP-3009 signature, not after: the guard's whole value is that declining it costs the
  // user nothing. Same (recipient, destChain) pair that is about to be bound into the auth nonce.
  await assertRecipientCodeAccepted({
    recipient: destinationAddress, destChain: destinationChain, confirm: confirmRecipientRisk,
  });

  // V9-1: compute the 32-byte CCTP mint recipient BEFORE signing so the SAME value feeds
  // both the EIP-3009 nonce derivation (below) and the bridgeWithAuth `recipient` arg (STEP 5).
  const mintRecipient = await addressToBytes32(destinationAddress, destinationChain);

  const validAfter = 0;
  const validBefore = Math.floor(Date.now() / 1000) + 900; // 15 min
  // V9-1: bind the destination into the EIP-3009 nonce so a relayer/front-runner cannot
  // redirect the burn. MUST byte-match the contract's _authBindingHash (abi.encode of
  // (bytes32 recipient, uint32 destDomain, bool payNative, uint256 quoteNonce)).
  const authNonce = keccak256(
    AbiCoder.defaultAbiCoder().encode(
      ['bytes32', 'uint32', 'bool', 'uint256'],
      [mintRecipient, quote.destDomain, payNative, quote.nonce]
    )
  );

  // Fetch and cache USDC domain params (name, version) on first call per chain
  if (!domainSeparatorCache.has(evmChain)) {
    const usdcDS = new Contract(config.usdcToken, USDC_DS_ABI, provider);
    const [onChainDS, usdcName, usdcVersion] = await Promise.all([
      usdcDS.DOMAIN_SEPARATOR(),
      usdcDS.name(),
      usdcDS.version(),
    ]);
    const dynamicDomain = {
      name: usdcName,
      version: usdcVersion,
      chainId: expectedChainId,
      verifyingContract: config.usdcToken,
    };
    const expectedDS = TypedDataEncoder.hashDomain(dynamicDomain);
    if (onChainDS !== expectedDS) {
      throw new Error(
        `USDC DOMAIN_SEPARATOR mismatch on ${evmChain}. On-chain: ${onChainDS}, expected: ${expectedDS}`
      );
    }
    domainSeparatorCache.set(evmChain, { domainSeparator: onChainDS, domain: dynamicDomain });
    console.log('DOMAIN_SEPARATOR_CACHED', { chain: evmChain, domainSeparator: onChainDS, name: usdcName, version: usdcVersion });
  }

  const { domain } = domainSeparatorCache.get(evmChain)!;

  const types = {
    ReceiveWithAuthorization: [
      { name: 'from', type: 'address' },
      { name: 'to', type: 'address' },
      { name: 'value', type: 'uint256' },
      { name: 'validAfter', type: 'uint256' },
      { name: 'validBefore', type: 'uint256' },
      { name: 'nonce', type: 'bytes32' },
    ],
  };

  const authValue = {
    from: signerAddress,
    to: bridgeContractAddress,
    value: amountBigInt,
    validAfter,
    validBefore,
    nonce: authNonce,
  };

  console.log('EIP712_SIGN_REQUEST', {
    domain,
    types,
    authValue: { ...authValue, value: authValue.value.toString() },
  });

  const authSig = await signer.signTypedData(domain, types, authValue);

  // ── Smart-contract-account guard (#137 R12) ────────────────────────────────
  // A contract account (Base Account, Safe, any ERC-4337 wallet) cannot produce an ECDSA signature
  // that recovers to its own address. It returns either an EIP-1271 payload or — when the account is
  // not yet deployed on THIS (the source) chain — an ERC-6492-wrapped one. Neither is 65 bytes, so
  // `Signature.from()` below throws `invalid raw signature length`: accurate, but meaningless to a user
  // who just wants to bridge, and it gives them no idea what to do next.
  //
  // This is reachable in production TODAY, and not via `preference`. The SDK restores a STORED signer
  // type in its constructor (CoinbaseWalletProvider.js:39/41) and only consults `preference` when no
  // signer was restored (:75 → :159). So our `preference: 'eoaOnly'` pin is never re-evaluated for a
  // returning user. Anyone who connected a smart wallet on this origin between e3cbdd5 (2026-05-07,
  // connector added with NO preference → SDK default 'all') and 3b2f446 (2026-06-03, pin added) still
  // has `scw` in localStorage and lands here.
  //
  // Nothing is at risk — this throws before any transaction is built, so no gas is spent and no funds
  // move. It is purely that the user cannot bridge and is told why in hex-decoder language.
  //
  // The REAL fix is to route contract accounts to `bridge()`, which authenticates on
  // `quote.userAddress == msg.sender` (ParseCctpBridge.sol:389) and needs no user signature at all —
  // it works for deployed accounts, undeployed accounts and EOAs alike. That path needs an approve tx
  // and new two-transaction UI state, so it is #137 Phase 1, not this guard. Until then, fail with an
  // actionable message instead of a cryptic one.
  //
  // Do NOT "fix" this by loosening the length check: EIP-3009 genuinely cannot work for an undeployed
  // contract account. Circle's SignatureChecker has no ERC-6492 branch, so it falls through to
  // ECRecover, which requires exactly 65 bytes (circlefin/stablecoin-evm#470, open since 2024-08-15,
  // unacknowledged). Coinbase's own x402 spec routes smart wallets away from EIP-3009 for this reason.
  const SIG_BYTES = 65;
  if (typeof authSig === 'string' && (authSig.length - 2) / 2 !== SIG_BYTES) {
    // ── The message MUST depend on WHY we are on this path (#137) ────────────────────────────────
    // Reaching here means a contract account produced a non-65-byte signature. There are two very
    // different reasons that can happen, and one shared message was actively harmful for one of them:
    //
    //   1. This build genuinely cannot route contract accounts (flag off, or a proven-EOA verdict
    //      that was wrong). "Connect a standard wallet" is correct and actionable.
    //   2. This build CAN route contract accounts, but `resolveEvmBridgeRoute` could not read
    //      bytecode and fell back to bridgeWithAuth (`bytecode-read-failed`, smartAccount.ts:214-219).
    //      Nothing is wrong with the user's wallet — an RPC read failed. Telling them to "connect a
    //      standard wallet" asks them to MOVE FUNDS to a different account on a false premise, and a
    //      passkey account has no seed phrase to import anywhere, so the instruction is not even
    //      followable. A retry would very likely just work.
    //
    // Case 2 is the only place in the app where our copy prompts a funds-moving action on a premise
    // we know to be false, so it gets its own message rather than a shared approximation.
    const readFailed = routeDecision.reason === 'bytecode-read-failed';
    throw new Error(
      readFailed
        ? 'We couldn’t verify your account type just now — this is usually a temporary network ' +
          'problem, not a problem with your wallet. Please try again in a moment. Nothing was sent ' +
          'and no fees were charged.'
        : 'This wallet is a smart contract account, which cannot sign the gasless bridge authorization. ' +
          'Connect a standard wallet (MetaMask, Rabby, or a Coinbase/Base account created from a private ' +
          'key rather than a passkey) to bridge. Smart account support is in progress.',
    );
  }

  const { v: authV, r: authR, s: authS } = Signature.from(authSig);

  console.log('EIP712_SIGNED', {
    v: authV,
    r: authR,
    s: authS,
  });

  // ─────────────────────────────────────────────────────────────────────
  // STEP 5: Call ParseCctpBridge.bridgeWithAuth()
  // ─────────────────────────────────────────────────────────────────────
  setStep('burning');

  // V9-1: `mintRecipient` was computed above STEP 4 and is reused verbatim here so the
  // value bound into the EIP-3009 nonce is the exact value passed to bridgeWithAuth.

  // Create bridge contract instance
  const bridgeContract = new Contract(bridgeContractAddress, PARSE_BRIDGE_ABI, signer);

  // Prepare quote tuple for contract call
  const quoteTuple = {
    destDomain: quote.destDomain,
    networkFeeUsdc: quote.networkFeeUsdc,
    networkFeeNative: quote.networkFeeNative,
    percentageFeeBps: quote.percentageFeeBps,
    timestamp: quote.timestamp,
    nonce: quote.nonce,
    userAddress: quote.userAddress,
    maxFee: quote.maxFee,
    minFinalityThreshold: quote.minFinalityThreshold,
    referrerAddress: quote.referrerAddress || '0x0000000000000000000000000000000000000000',
    referrerShareBps: quote.referrerShareBps ?? 0,
  };

  // Prepare AuthParams struct.
  // V9-1: `nonce` is intentionally absent — the contract recomputes the binding nonce on-chain
  // from (recipient, destDomain, payNative, quote.nonce). Supplying it would not match the struct.
  const authParams = {
    validAfter,
    validBefore,
    v: authV,
    r: authR,
    s: authS,
  };

  // Calculate native value to send if paying network fee in ETH
  const nativeValue = payNative ? BigInt(quote.networkFeeNative) : 0n;

  console.log('BRIDGE_WITH_AUTH_CALL', {
    amount: amountBigInt.toString(),
    recipient: mintRecipient,
    quote: quoteTuple,
    payNative,
    nativeValue: nativeValue.toString(),
    authParams,
    derivedAuthNonce: authNonce, // V9-1: recomputed on-chain; logged for parity debugging
    breakdown: {
      percentageFee: breakdown.percentageFee,
      networkFeeUsdc: breakdown.networkFeeUsdc,
      totalFee: breakdown.totalFeeUsdc,
      netAmount: breakdown.netAmount,
    },
  });

  // Re-check quote hasn't expired right before the transaction
  const currentTime = Math.floor(Date.now() / 1000);
  if (currentTime > expiresAt) {
    throw new Error('Quote expired during transaction preparation. Please try again.');
  }

  // Execute bridge transaction (approval-free via EIP-3009)
  const bridgeTx = await bridgeContract.bridgeWithAuth(
    amountBigInt,
    mintRecipient,
    quoteTuple,
    signature,       // operator signature from quote
    payNative,
    authParams,
    { value: nativeValue }
  );

  console.log('BRIDGE_TX_SENT', { hash: bridgeTx.hash });

  const { hash } = await waitForBurnReceiptBounded(bridgeTx, 'bridgeWithAuth');

  return { txHash: hash, networkFeeUsdc: breakdown.networkFeeUsdc, nativePriceUsd: breakdown.nativePrice };
}

/**
 * Execute Solana burn transaction via FBV01 bridge program
 *
 * Flow:
 * 1. Fetch signed quote from backend API (Ed25519 signature)
 * 2. Validate quote (expiry, user address)
 * 3. Build 136-byte quote message + Ed25519 verify instruction
 * 4. Build FBV01 bridge instruction via Anchor IDL (11 core + 12 remaining CCTP accounts)
 * 5. Build VersionedTransaction with ALT, sign, send
 */
async function executeSolanaBurn(
  params: Omit<BurnParams, 'sourceChain'>,
  setStep: (step: BurnStep) => void,
  solanaWallet: {
    publicKey: PublicKey;
    signTransaction: <T extends VersionedTransaction>(tx: T) => Promise<T>;
  },
  anchorWallet: AnchorWallet
): Promise<BurnResult> {
  const {
    amount,
    destinationChain,
    destinationAddress,
    payNative = false,
    confirmRecipientRisk,
  } = params;

  // ── #144 GUARD — signing path 3 of 3, and the one that matters most ─────────────────────────────
  // This is #144's HEADLINE path: source Solana, destination EVM, a Safe connected over
  // WalletConnect silently becomes the recipient with ZERO user action. The originally-proposed
  // predicate — hasCode(recipient, sourceChain) — is meaningless here (the source is Solana) and
  // would have no-opped on exactly this scenario, which is why the shared guard takes no source
  // argument. It also passes #143 untouched, because the Safe genuinely IS the connected wallet.
  // Placed before the quote fetch so declining costs nothing at all.
  await assertRecipientCodeAccepted({
    recipient: destinationAddress, destChain: destinationChain, confirm: confirmRecipientRisk,
  });

  const connection = new Connection(SOLANA_RPC_URL, 'confirmed');

  const cctpConfig = CCTP_CONFIG.solana;
  const userPublicKey = solanaWallet.publicKey;

  // Convert amount (USDC has 6 decimals)
  const amountBN = BigInt(Math.floor(parseFloat(amount) * 1_000_000));

  // ─────────────────────────────────────────────────────────────────────
  // STEP 1: Fetch signed quote from backend
  // ─────────────────────────────────────────────────────────────────────
  setStep('fetching_quote');

  const quoteResponse = await fetchQuote(
    'solana',
    destinationChain,
    amountBN.toString(),
    userPublicKey.toBase58()
  );

  if (!quoteResponse.success || !quoteResponse.data) {
    throw new Error(quoteResponse.error || 'Failed to fetch quote from server');
  }

  const { quote, signature, expiresAt, operatorPubkey, breakdown } = quoteResponse.data;

  if (!operatorPubkey) {
    throw new Error('Quote response missing operatorPubkey for Solana');
  }

  console.log('SOLANA_QUOTE_RECEIVED', {
    quote,
    operatorPubkey,
    expiresAt,
    breakdown,
    timeUntilExpiry: expiresAt - Math.floor(Date.now() / 1000),
  });

  // Displayed-vs-signed cross-check (#161 item 2) — covers BOTH Solana submit sites, which read the
  // same `quote` object in two different shapes: the Ed25519 quote message (writeU64LE over
  // BigInt(quote.networkFeeUsdc), via buildSolanaQuoteMessage below) and the Anchor instruction arg
  // (new BN(quote.networkFeeUsdc)). Placed before either is built, and `quote` is never mutated
  // between here and them, so one check pins both. normalizeAtomic compares by VALUE, so the
  // BN/u64/string shape difference cannot produce a false pass or a false reject.
  assertQuoteMatchesBreakdown(quote, breakdown);

  // ─────────────────────────────────────────────────────────────────────
  // STEP 2: Validate quote
  // ─────────────────────────────────────────────────────────────────────
  if (quote.userAddress !== userPublicKey.toBase58()) {
    throw new Error('Quote user address mismatch');
  }

  const timeRemaining = expiresAt - Math.floor(Date.now() / 1000);
  if (timeRemaining < 0) {
    throw new Error('Quote has expired. Please request a new quote.');
  }
  if (timeRemaining < 120) {
    console.warn('QUOTE_EXPIRING_SOON', { timeRemaining });
    toast.warning(`Quote expires in ${Math.floor(timeRemaining / 60)}m ${timeRemaining % 60}s`);
  }

  // ─────────────────────────────────────────────────────────────────────
  // STEP 3: Build Ed25519 verify instruction
  // ─────────────────────────────────────────────────────────────────────
  setStep('burning');

  const operatorPubkeyBytes = decodeBase58(operatorPubkey);
  const signatureBytes = decodeBase58(signature);
  const quoteMessage = buildSolanaQuoteMessage(quote, userPublicKey);

  console.log('Ed25519 verify params:', {
    operatorPubkey,
    signatureHex: bytesToHex(signatureBytes),
    messageHex: bytesToHex(quoteMessage),
    messageLength: quoteMessage.length,
  });

  const ed25519Ix = Ed25519Program.createInstructionWithPublicKey({
    publicKey: operatorPubkeyBytes,
    message: quoteMessage,
    signature: signatureBytes,
  });

  // ─────────────────────────────────────────────────────────────────────
  // STEP 4: Derive all accounts for FBV01 bridge instruction
  // ─────────────────────────────────────────────────────────────────────
  const bridgeProgramId = new PublicKey(SOLANA_BRIDGE_CONFIG.programId);
  const usdcMint = new PublicKey(cctpConfig.usdcMint);
  const tokenMessengerMinter = new PublicKey(cctpConfig.tokenMessengerMinterProgram);
  const messageTransmitter = new PublicKey(cctpConfig.messageTransmitterProgram);
  const destinationDomain = getDomainForChain(destinationChain);

  // FBV01 core PDAs
  const [statePda] = PublicKey.findProgramAddressSync(
    [stringToBytes('state')],
    bridgeProgramId
  );

  // Derive nonce page PDA: seeds = ["nonces", user, page_index_le_bytes]
  // Page is determined by nonce value: page = floor(nonce / 4096)
  const NONCES_PER_PAGE = 4096;
  const pageIndex = Math.floor(quote.nonce / NONCES_PER_PAGE);
  const pageIndexBuffer = new Uint8Array(8);
  new DataView(pageIndexBuffer.buffer).setBigUint64(0, BigInt(pageIndex), true); // little-endian

  const [userNoncesPda] = PublicKey.findProgramAddressSync(
    [stringToBytes('nonces'), userPublicKey.toBytes(), pageIndexBuffer],
    bridgeProgramId
  );

  // User's USDC token account
  const userUsdcAccount = await getAssociatedTokenAddress(usdcMint, userPublicKey);

  // Bridge PDA's USDC token account (state PDA is the authority)
  const bridgeUsdcAccount = await getAssociatedTokenAddress(usdcMint, statePda, true);

  // Treasury USDC token accounts
  const treasuryA = new PublicKey(SOLANA_BRIDGE_CONFIG.treasuryA);
  const treasuryB = new PublicKey(SOLANA_BRIDGE_CONFIG.treasuryB);
  const treasuryAUsdc = await getAssociatedTokenAddress(usdcMint, treasuryA, true);
  const treasuryBUsdc = await getAssociatedTokenAddress(usdcMint, treasuryB, true);

  // Referrer USDC ATA — derive from quote's referrerAddress when present
  const referrerUsdcAccount = await (async () => {
    const referrerAddr = quote.referrerAddress;
    if (referrerAddr && referrerAddr !== PublicKey.default.toBase58()) {
      return getAssociatedTokenAddress(
        usdcMint,
        new PublicKey(referrerAddr),
        true, // allowOwnerOffCurve
      );
    }
    return treasuryAUsdc; // placeholder when no referrer
  })();

  // CCTP PDAs
  const [tokenMessengerPda] = PublicKey.findProgramAddressSync(
    [stringToBytes('token_messenger')],
    tokenMessengerMinter
  );

  const [tokenMinterPda] = PublicKey.findProgramAddressSync(
    [stringToBytes('token_minter')],
    tokenMessengerMinter
  );

  const [localTokenPda] = PublicKey.findProgramAddressSync(
    [stringToBytes('local_token'), usdcMint.toBuffer()],
    tokenMessengerMinter
  );

  const [remoteTokenMessengerPda] = PublicKey.findProgramAddressSync(
    [stringToBytes('remote_token_messenger'), stringToBytes(destinationDomain.toString())],
    tokenMessengerMinter
  );

  const [messageTransmitterStatePda] = PublicKey.findProgramAddressSync(
    [stringToBytes('message_transmitter')],
    messageTransmitter
  );

  const [senderAuthPda] = PublicKey.findProgramAddressSync(
    [stringToBytes('sender_authority')],
    tokenMessengerMinter
  );

  const [eventAuthorityPda] = PublicKey.findProgramAddressSync(
    [stringToBytes('__event_authority')],
    tokenMessengerMinter
  );

  // Denylist account PDA (derived from state PDA as "owner" in CCTP context)
  const [denylistPda] = PublicKey.findProgramAddressSync(
    [stringToBytes('denylist_account'), statePda.toBuffer()],
    tokenMessengerMinter
  );

  // Generate keypair for message sent event data account
  const messageSentEventDataKeypair = Keypair.generate();

  // Convert destination address to raw bytes for recipient
  const mintRecipientBytes = addressToBytes32Raw(destinationAddress, destinationChain);

  // CCTP V2 parameters — always fast mode
  const maxFee = BigInt(Math.max(Math.floor(Number(amountBN) * 0.001), 1000));
  const minFinalityThreshold = 1000;

  console.log('FBV01 bridge params:', {
    amount: amountBN.toString(),
    destinationDomain,
    mintRecipient: bytesToHex(mintRecipientBytes),
    payNative,
    maxFee: maxFee.toString(),
    minFinalityThreshold,
    statePda: statePda.toBase58(),
    bridgeUsdc: bridgeUsdcAccount.toBase58(),
    userUsdc: userUsdcAccount.toBase58(),
  });

  // ─────────────────────────────────────────────────────────────────────
  // STEP 5: Build FBV01 bridge instruction via Anchor
  // ─────────────────────────────────────────────────────────────────────

  // Create Anchor provider from wallet adapter
  const anchorProvider = new AnchorProvider(connection, anchorWallet, { commitment: 'confirmed' });
  // Make SOLANA_BRIDGE_CONFIG.programId authoritative: Anchor reads the program id from idl.address (still the devnet
  // 4NnQ…), but the PDAs above derive from bridgeProgramId. On mainnet both must be Dt5N39… — override so the bridge
  // ix can never target the devnet program while PDAs derive from the mainnet one.
  const bridgeProgram = new Program({ ...(fbv02Idl as any), address: SOLANA_BRIDGE_CONFIG.programId } as any, anchorProvider);

  // Build bridge instruction with Anchor
  // Core accounts (11) are specified in .accounts()
  // CCTP accounts (12) are passed via .remainingAccounts()
  const bridgeIx = await (bridgeProgram.methods as any).bridge(
    new BN(amountBN.toString()),                              // amount: u64
    Array.from(mintRecipientBytes),                            // recipient: [u8; 32]
    {                                                          // quote: Quote
      destDomain: quote.destDomain,
      networkFeeUsdc: new BN(quote.networkFeeUsdc),
      networkFeeNative: new BN(quote.networkFeeNative),
      percentageFeeBps: quote.percentageFeeBps,
      timestamp: new BN(quote.timestamp),
      nonce: new BN(quote.nonce),
      userAddress: userPublicKey,
      referrerAddress: quote.referrerAddress
        ? new PublicKey(quote.referrerAddress)
        : PublicKey.default,
      referrerShareBps: quote.referrerShareBps ?? 0,
    },
    Array.from(signatureBytes),                                // signature: [u8; 64]
    payNative,                                                 // pay_native: bool
    new BN(maxFee.toString()),                                 // max_fee: u64
    minFinalityThreshold,                                      // min_finality_threshold: u32
  )
    .accounts({
      state: statePda,
      userNonces: userNoncesPda,
      user: userPublicKey,
      feePayer: userPublicKey,
      userUsdc: userUsdcAccount,
      bridgeUsdc: bridgeUsdcAccount,
      treasuryAUsdc: treasuryAUsdc,
      treasuryBUsdc: treasuryBUsdc,
      treasuryB: treasuryB,
      referrerUsdc: referrerUsdcAccount,
      tokenProgram: TOKEN_PROGRAM_ID,
      systemProgram: SystemProgram.programId,
      instructionsSysvar: SYSVAR_INSTRUCTIONS_PUBKEY,
    })
    .remainingAccounts([
      // [0]  cctp_token_messenger_minter - TMM program ID
      { pubkey: tokenMessengerMinter, isSigner: false, isWritable: false },
      // [1]  cctp_message_transmitter - MT program ID
      { pubkey: messageTransmitter, isSigner: false, isWritable: false },
      // [2]  token_messenger - PDA from TMM
      { pubkey: tokenMessengerPda, isSigner: false, isWritable: false },
      // [3]  remote_token_messenger - PDA from TMM
      { pubkey: remoteTokenMessengerPda, isSigner: false, isWritable: false },
      // [4]  token_minter - PDA from TMM
      { pubkey: tokenMinterPda, isSigner: false, isWritable: false },
      // [5]  local_token - PDA from TMM (writable)
      { pubkey: localTokenPda, isSigner: false, isWritable: true },
      // [6]  message_transmitter_state - PDA from MT (writable)
      { pubkey: messageTransmitterStatePda, isSigner: false, isWritable: true },
      // [7]  message_sent_event_data - keypair (writable, signer)
      { pubkey: messageSentEventDataKeypair.publicKey, isSigner: true, isWritable: true },
      // [8]  usdc_mint (writable)
      { pubkey: usdcMint, isSigner: false, isWritable: true },
      // [9]  cctp_authority - sender_authority PDA from TMM
      { pubkey: senderAuthPda, isSigner: false, isWritable: false },
      // [10] event_authority - PDA from TMM
      { pubkey: eventAuthorityPda, isSigner: false, isWritable: false },
      // [11] denylist_account - PDA from TMM (may not exist on-chain)
      { pubkey: denylistPda, isSigner: false, isWritable: false },
    ])
    .instruction();

  // ─────────────────────────────────────────────────────────────────────
  // STEP 6: Build VersionedTransaction with ALT
  // ─────────────────────────────────────────────────────────────────────

  // Fetch the Address Lookup Table
  const altAddress = new PublicKey(SOLANA_BRIDGE_CONFIG.altAddress);
  const altAccountInfo = await connection.getAddressLookupTable(altAddress);

  if (!altAccountInfo.value) {
    throw new Error(`Address Lookup Table not found: ${SOLANA_BRIDGE_CONFIG.altAddress}`);
  }

  const lookupTable: AddressLookupTableAccount = altAccountInfo.value;

  // Get recent blockhash
  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');

  // Build V0 message with ALT
  // Instructions: [Ed25519 verify, FBV01 bridge]
  const messageV0 = new TransactionMessage({
    payerKey: userPublicKey,
    recentBlockhash: blockhash,
    instructions: [ed25519Ix, bridgeIx],
  }).compileToV0Message([lookupTable]);

  const versionedTx = new VersionedTransaction(messageV0);

  // Partial sign with the message sent event data keypair
  versionedTx.sign([messageSentEventDataKeypair]);

  console.log('Signing Solana FBV01 bridge transaction...');

  // Sign with user's wallet via wallet adapter
  const signedTx = await solanaWallet.signTransaction(versionedTx);

  console.log('Sending Solana FBV01 bridge transaction...');

  // Send transaction
  const txHash = await connection.sendRawTransaction(signedTx.serialize(), {
    skipPreflight: false,
    preflightCommitment: 'confirmed',
  });

  console.log('Solana FBV01 bridge tx broadcast:', txHash);

  // Confirm transaction
  await connection.confirmTransaction(
    {
      signature: txHash,
      blockhash,
      lastValidBlockHeight,
    },
    'confirmed'
  );

  console.log('Solana FBV01 bridge tx confirmed:', txHash);

  return { txHash, networkFeeUsdc: breakdown.networkFeeUsdc, nativePriceUsd: breakdown.nativePrice };
}

/**
 * Hook for burning USDC on EVM and Solana chains
 */
export function useBurn() {
  const [state, setState] = useState<BurnState>({
    isBurning: false,
    burnStep: 'idle',
    burnTxHash: null,
    burnError: null,
  });

  // Solana wallet hooks — must be at top level (React rules of hooks)
  const { signTransaction: solanaSignTransaction, publicKey: solanaPublicKey } = useSolanaWalletHook();
  const anchorWallet = useAnchorWallet();

  const reset = useCallback(() => {
    setState({
      isBurning: false,
      burnStep: 'idle',
      burnTxHash: null,
      burnError: null,
    });
  }, []);

  const setStep = useCallback((step: BurnStep) => {
    setState((s) => ({ ...s, burnStep: step }));
  }, []);

  const executeBurn = useCallback(
    async (params: BurnParams): Promise<BurnResult> => {
      const { sourceChain, amount, destinationChain, destinationAddress } = params;

      console.log('EXECUTE_BURN_ENTRY', {
        params: {
          sourceChain: params.sourceChain,
          destinationChain: params.destinationChain,
          amount: params.amount,
          destinationAddress: params.destinationAddress?.substring(0, 10)
        },
        expectedChainIds: {
          ethereum: 11155111,
          base: 84532,
          optimism: 11155420,
          arbitrum: 421614,
          polygon: 80002,
        }
      });

      setState((s) => ({
        ...s,
        isBurning: true,
        burnStep: 'idle',
        burnError: null,
        burnTxHash: null,
      }));

      try {
        let burnResult: BurnResult;

        if (isEvmChain(sourceChain)) {
          // EVM burn (Ethereum, Base)
          burnResult = await executeEvmBurn(params, setStep);
        } else if (isSolanaChain(sourceChain)) {
          // Solana burn via FBV01 bridge
          if (!solanaPublicKey || !solanaSignTransaction || !anchorWallet) {
            throw new Error('Solana wallet not connected. Please connect first.');
          }
          burnResult = await executeSolanaBurn(
            {
              amount,
              destinationChain,
              destinationAddress,
              payNative: params.payNative,
              confirmRecipientRisk: params.confirmRecipientRisk,
            },
            setStep,
            {
              publicKey: solanaPublicKey,
              signTransaction: solanaSignTransaction,
            },
            anchorWallet
          );
        } else {
          throw new Error(`Unsupported source chain: ${sourceChain}`);
        }

        // Success
        setState((s) => ({
          ...s,
          isBurning: false,
          burnStep: 'complete',
          burnTxHash: burnResult.txHash,
        }));

        return burnResult;
      } catch (error) {
        const errorMessage = getErrorMessage(error);
        console.error('Burn error:', errorMessage);

        // Check if user rejected using utility function.
        // A declined #144 recipient warning is a DELIBERATE user cancellation, not a failure: nothing
        // was signed, nothing was broadcast, no funds moved. Surfacing it as "Transfer failed. Please
        // try again." would both misdescribe it and nag the user to retry the exact thing they just
        // chose not to do.
        const userRejected =
          isUserRejection(error) || error instanceof RecipientRiskDeclinedError;

        // Check for insufficient balance. The pre-sign USDC check throws "Insufficient USDC balance: …"
        // (note the "USDC"), which does NOT contain the substring "Insufficient balance" — so match it
        // explicitly, otherwise this falls through to the generic "Transfer failed" toast (#102).
        const insufficientBalance =
          errorMessage.includes('Insufficient balance') ||
          errorMessage.includes('Insufficient USDC balance') ||
          errorMessage.includes('insufficient funds');

        // DEV-375 (GATE-2 amend, TOAST PREDICATE ONLY) — a receipt-read failure is UNKNOWN, not
        // failed. This is deterministic on base/optimism/arbitrum for contract-account bridges,
        // via the same `approveErc20Exact` receipt wait that produces `RECEIPT_UNKNOWN_MARKER`
        // elsewhere in this change (erc20Approve.ts), and it may well have succeeded on-chain — so
        // "Transfer failed. Please try again." both asserts a failure that did not occur and invites
        // a duplicate spend. `burnError` below (getDisplayError) already carries the marker-bearing
        // message verbatim for this case; checking the raw `errorMessage` here is equivalent since
        // the marker is embedded in the thrown message before classification.
        const receiptUnknown = isReceiptUnknownMessage(errorMessage);

        // Show appropriate toast
        if (userRejected) {
          toast.error('Transaction cancelled.');
        } else if (insufficientBalance) {
          toast.error('Insufficient USDC balance.');
        } else if (receiptUnknown) {
          // DEV-375 finding #5 — the ONLY reachable producer of a marker-bearing `burnError` on this
          // path is `approveErc20Exact` (called above, BEFORE `setStep('burning')`), so no USDC has
          // moved: `burnTxHash` is still null and `waitBounded` hardcodes `valueMoving:false`
          // precisely so the approve copy drops transfer framing. "Couldn't confirm the transfer"
          // reintroduces the false transfer claim finding #8 removed, one layer up.
          toast.warning("Couldn't confirm the USDC approval on-chain — no transfer was sent. Check a block explorer before retrying.");
        } else {
          toast.error('Transfer failed. Please try again.');
        }

        setState((s) => ({
          ...s,
          isBurning: false,
          burnStep: 'error',
          burnError: userRejected
            ? 'Transaction cancelled by user'
            : getDisplayError(error, sourceChain) || 'Burn transaction failed',
        }));

        throw error;
      }
    },
    [setStep, solanaPublicKey, solanaSignTransaction, anchorWallet]
  );

  return {
    ...state,
    executeBurn,
    reset,
  };
}
